import { buildAgentsSearchUrl } from "~/lib/marketplace/constants";
import { MOONLIGHT_CATEGORIES } from "~/lib/marketplace/moonlight-home";
import {
  MARKETING_BUILD_CUSTOM,
  MARKETING_CASE_STUDIES,
  MARKETING_CUSTOM_AGENT_DEVELOPMENT,
  MARKETING_CUSTOMER_STORIES,
  MARKETING_PRODUCT_INTEGRATIONS,
  MARKETING_PRODUCT_PROFILE,
  MARKETING_PRODUCT_RESPONSIBILITIES,
  MARKETING_PRODUCT_TOOLS,
  MARKETING_PRODUCT_TRAINING,
} from "~/lib/inbound-gpt/paths";

/** @deprecated Legacy route — prefer MARKETING_HIRE_ONBOARDING. */
export const MARKETING_EMPLOYERS_POST_AI_JOB = "/employers/post-ai-job";

/** Configure → test → deploy onboarding (custom hire builder). */
export const MARKETING_HIRE_ONBOARDING = MARKETING_BUILD_CUSTOM;
export const MARKETING_BECOME_PARTNER = "/employers/become-a-partner";

export type MarketingMegaLink = {
  label: string;
  href: string;
  description?: string;
};

export type MarketingMegaFeature = {
  title: string;
  description: string;
  href: string;
  icon: "search" | "clone" | "shield" | "briefcase" | "handshake" | "chart";
};

export type ProductMegaIcon =
  | "hitl"
  | "handoff"
  | "takeover"
  | "phoneRules"
  | "howItWorks"
  | "profile"
  | "responsibilities"
  | "training"
  | "tools"
  | "integrations";

export type ProductMegaGroup = "journey" | "collaboration" | "platform";

export type ProductMegaItem = {
  label: string;
  description: string;
  href: string;
  icon: ProductMegaIcon;
  /** Optional group for SiteGlue Platform mega menu sections. */
  group?: ProductMegaGroup;
};

/** Product mega menu — Inbound GPT defaults. */
export const PRODUCT_MEGA_ITEMS: ProductMegaItem[] = [
  {
    label: "Profile",
    description: "Persona, team members, locations, and voice for your AI agent.",
    href: MARKETING_PRODUCT_PROFILE,
    icon: "profile",
  },
  {
    label: "Responsibilities",
    description: "Channels your agent handles — live chat, phone, text, email, and more.",
    href: MARKETING_PRODUCT_RESPONSIBILITIES,
    icon: "responsibilities",
  },
  {
    label: "Training",
    description: "Company profile, knowledge bases, RAG sources, and test tools.",
    href: MARKETING_PRODUCT_TRAINING,
    icon: "training",
  },
  {
    label: "Tools",
    description: "Appointments, MCP servers, and integrations with your existing systems.",
    href: MARKETING_PRODUCT_TOOLS,
    icon: "tools",
  },
  {
    label: "Integrations",
    description: "Connect calendars, EMR/PMS, Zoom, and custom MCP workflows.",
    href: MARKETING_PRODUCT_INTEGRATIONS,
    icon: "integrations",
  },
];

/** SiteGlue Platform mega — collaboration hierarchy + AI Employee platform. */
export const SITEGLUE_PRODUCT_MEGA_ITEMS: ProductMegaItem[] = [
  {
    label: "How Onboarding Works",
    description:
      "Set up a SiteGlue Agent with Trish—from your website and channels to launch.",
    href: "/how-it-works",
    icon: "howItWorks",
    group: "journey",
  },
  {
    label: "Human-in-the-Loop™",
    description:
      "Review, monitor, guide, and approve how your SiteGlue Agent works with your team.",
    href: "/human-in-the-loop",
    icon: "hitl",
    group: "collaboration",
  },
  {
    label: "Team Handoff™",
    description:
      "Define when and how your SiteGlue Agent hands conversations to the right team member.",
    href: "/team-handoff",
    icon: "handoff",
    group: "collaboration",
  },
  {
    label: "Team Takeover™",
    description:
      "Let your team instantly continue AI-led conversations without losing context.",
    href: "/team-takeover",
    icon: "takeover",
    group: "collaboration",
  },
  {
    label: "Phone Handling Rules",
    description:
      "Route calls by intent, location, office status, caller type, and business policy.",
    href: "/phone-handling-rules",
    icon: "phoneRules",
    group: "collaboration",
  },
  {
    label: "Agent Profile",
    description: "Persona, team members, locations, and voice for your SiteGlue Agent.",
    href: MARKETING_PRODUCT_PROFILE,
    icon: "profile",
    group: "platform",
  },
  {
    label: "Communication Channels",
    description: "Website chat, phone, SMS, email, and more — with Team Handoff™.",
    href: MARKETING_PRODUCT_RESPONSIBILITIES,
    icon: "responsibilities",
    group: "platform",
  },
  {
    label: "AI Skills",
    description: "Scheduling, Team Handoff™, and the tools your SiteGlue Agent uses to get work done.",
    href: MARKETING_PRODUCT_TOOLS,
    icon: "tools",
    group: "platform",
  },
  {
    label: "Knowledge & Training",
    description: "Company profile, knowledge bases, RAG sources, and test tools.",
    href: MARKETING_PRODUCT_TRAINING,
    icon: "training",
    group: "platform",
  },
  {
    label: "Integrations",
    description: "Connect calendars, EMR/PMS, Zoom, and custom MCP workflows.",
    href: MARKETING_PRODUCT_INTEGRATIONS,
    icon: "integrations",
    group: "platform",
  },
];

export const SITEGLUE_PRODUCT_MEGA_GROUP_LABELS: Record<
  Exclude<ProductMegaGroup, "journey">,
  string
> = {
  collaboration: "Human Collaboration",
  platform: "SiteGlue Agent",
};

/** Brand-aware Product mega items (SiteGlue vs Inbound GPT). */
export function getProductMegaItems(brandId?: string): ProductMegaItem[] {
  return brandId === "siteglue" ? SITEGLUE_PRODUCT_MEGA_ITEMS : PRODUCT_MEGA_ITEMS;
}

export const PRODUCT_MEGA_SEE_ALL_HREF = "/features";
/** SiteGlue: mega “see all” points at the flagship Platform page. */
export const SITEGLUE_PLATFORM_MEGA_SEE_ALL_HREF = "/platform";
export const PRODUCT_MEGA_CUSTOM_DEV_HREF = MARKETING_CUSTOM_AGENT_DEVELOPMENT;
export const AGENT_TEMPLATES_REQUEST_ROLE_HREF = MARKETING_CUSTOM_AGENT_DEVELOPMENT;

/** Agent Templates mega menu — Industries column (matches /agents category pills). */
export const CASE_STUDIES_INDUSTRIES: MarketingMegaLink[] = MOONLIGHT_CATEGORIES.filter(
  (cat) => cat.id !== "all",
).map((cat) => ({
  label: cat.label,
  href: buildAgentsSearchUrl({ category: cat.id }),
}));

/** @deprecated Use CASE_STUDIES_INDUSTRIES */
export const AGENT_TEMPLATES_INDUSTRIES = CASE_STUDIES_INDUSTRIES;

/** Agent Templates mega menu — Roles column. */
export const CASE_STUDIES_ROLES: MarketingMegaLink[] = [
  {
    label: "Online Patient Coordinator",
    href: buildAgentsSearchUrl({ jobTitles: ["Online Patient Coordinator"] }),
  },
  {
    label: "Scheduling Specialist",
    href: buildAgentsSearchUrl({
      jobTitles: ["Scheduling Specialist", "Scheduling Coordinator"],
      role: "appointment_setter",
    }),
  },
  {
    label: "New Student Advisor",
    href: buildAgentsSearchUrl({ jobTitles: ["New Student Advisor"] }),
  },
  {
    label: "Online Support Specialist",
    href: buildAgentsSearchUrl({
      jobTitles: ["Online Support Specialist", "Online Support Agent"],
      role: "customer_service",
    }),
  },
  {
    label: "Front Desk Receptionist",
    href: buildAgentsSearchUrl({ jobTitles: ["Front Desk Receptionist"] }),
  },
  {
    label: "Patient Intake Specialist",
    href: buildAgentsSearchUrl({
      jobTitles: ["Patient Intake Specialist"],
      role: "client_intake",
    }),
  },
];

/** @deprecated Use CASE_STUDIES_ROLES */
export const AGENT_MARKETPLACE_POSITIONS = CASE_STUDIES_ROLES;

/** @deprecated Legacy feature cards — Case Studies mega menu uses Industries + Roles. */
export const AGENT_MARKETPLACE_FEATURES: MarketingMegaFeature[] = [
  {
    title: "Browse battle-tested agents",
    description:
      "Explore official Inbound GPT templates and Moonlight agents rated by real employers.",
    href: "/agents",
    icon: "search",
  },
  {
    title: "Clone & customize",
    description:
      "Hire a proven agent profile, then tailor responsibilities, tools, and training for your business.",
    href: "/agents/hire/custom",
    icon: "clone",
  },
  {
    title: "Live demos",
    description:
      "Try phone, chat, browser call, and video before you hire — every agent shows enabled channels.",
    href: "/features#agents",
    icon: "shield",
  },
];

export const CASE_STUDIES_SEE_ALL_HREF = MARKETING_CASE_STUDIES;
export const AGENT_TEMPLATES_SEE_ALL_HREF = "/agents";

/** Resources mega menu — docs, stories, discovery. */
export const RESOURCES_MEGA_LINKS: MarketingMegaLink[] = [
  {
    label: "Customer Stories",
    href: MARKETING_CUSTOMER_STORIES,
    description: "How businesses use Inbound GPT agents to never miss a conversation.",
  },
  {
    label: "Documentation",
    href: "/documentation",
    description: "Setup guides, channels, and integration walkthroughs.",
  },
  {
    label: "Blog",
    href: "/blog",
    description: "Product updates, playbooks, and industry tips.",
  },
  {
    label: "Announcements",
    href: "/announcements",
    description: "Release notes and product announcements.",
  },
  {
    label: "Help Center",
    href: "/help-center",
    description: "Answers for common questions and troubleshooting.",
  },
  {
    label: "API Reference",
    href: "/api-reference",
    description: "Webhooks, authentication, and developer resources.",
  },
  {
    label: "AI Summary",
    href: "/ai-summary",
    description: "Machine-readable overview for AI systems and researchers.",
  },
];

/** @deprecated Employers mega menu removed — use MARKETING_HIRE_ONBOARDING. */
export const EMPLOYERS_ACTIONS: MarketingMegaLink[] = [
  {
    label: "Build your agent",
    href: MARKETING_HIRE_ONBOARDING,
    description: "Describe what you need, add your website, and configure your Inbound GPT agent.",
  },
  {
    label: "Become a Partner",
    href: MARKETING_BECOME_PARTNER,
    description: "White-label, reseller, and affiliate programs for agencies and integrators.",
  },
];

/** @deprecated Employers mega menu removed. */
export const EMPLOYERS_FEATURES: MarketingMegaFeature[] = [
  {
    title: "Hire proven Inbound GPT agents",
    description:
      "Find and hire battle-tested agents for phone, chat, scheduling, and patient intake.",
    href: "/agents",
    icon: "briefcase",
  },
  {
    title: "Build your agent",
    description:
      "Describe what your agent should handle, add your website, and configure your Inbound GPT agent to test and deploy.",
    href: MARKETING_HIRE_ONBOARDING,
    icon: "chart",
  },
  {
    title: "Partner programs",
    description:
      "White-label, reseller, and affiliate options to bring Inbound GPT to your clients.",
    href: MARKETING_BECOME_PARTNER,
    icon: "handshake",
  },
];

export const POST_AI_JOB_SAMPLE_PROMPTS = [
  {
    label: "Telephone Receptionist",
    starter:
      "I need a Telephone Receptionist Inbound GPT agent to answer inbound calls, greet callers professionally, route calls to the right teammate, take messages, and schedule appointments when our front desk is busy.",
  },
  {
    label: "Online Patient Coordinator",
    starter:
      "I need an Online Patient Coordinator Inbound GPT agent to engage website visitors on live chat, answer questions about our services, qualify new and returning patients, and help schedule appointments online.",
  },
  {
    label: "New Patient Assistant",
    starter:
      "I need a New Patient Assistant Inbound GPT agent to welcome new patients, collect intake details, explain what to expect at their first visit, and book initial appointments so onboarding feels warm and effortless.",
  },
  {
    label: "Online Support Specialist",
    starter:
      "I need an Online Support Specialist Inbound GPT agent to answer common questions on live chat and email, troubleshoot routine requests, and connect visitors to a human teammate when they need extra help.",
  },
  {
    label: "Inbound Sales Rep",
    starter:
      "I need an Inbound Sales Rep Inbound GPT agent to respond instantly to website inquiries, qualify leads, answer product and pricing questions, and book sales calls with the right rep on our team.",
  },
] as const;

/** Channel options the Post AI Job assistant asks about during intake. */
export const POST_AI_JOB_RESPONSIBILITIES = [
  { value: "live_chat", label: "Live Chat" },
  { value: "phone_calls", label: "Phone Calls" },
  { value: "email", label: "Emails" },
  { value: "text_messages", label: "Text Messages" },
  { value: "video_calls", label: "Video Calls" },
  { value: "browser_calls", label: "Voice (WebRTC)" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "messenger", label: "Messenger" },
] as const;

/** @deprecated Use POST_AI_JOB_SAMPLE_PROMPTS */
export const POST_AI_JOB_EXAMPLE_PROMPTS = POST_AI_JOB_SAMPLE_PROMPTS.map((p) => p.label);

export const POST_AI_JOB_PENDING_PROMPT_KEY = "post-ai-job-pending-prompt";
export const POST_AI_JOB_SESSION_KEY = "post-ai-job-session";
